import { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"

import type { SegmentReport } from "../types"

import { getCompanyReport } from "../api/company-analysis-api"
import { mergePlanLibraryDocs } from "../utils/plan-library-docs"

export type { PlanLibraryDoc } from "../utils/plan-library-docs"

/**
 * Source library for GenerateModal: company report evidence + segment report URLs.
 * Empty when neither surface has resolvable sources (modal falls back to evidence copy).
 *
 * `enabled` must reflect the caller's edit rights. Plans are viewer-readable but
 * the company report is admin/owner-only, so an ungated fetch made every viewer
 * issue a request that could only ever 403 — noise in the console and the logs
 * for data they are never shown, since GenerateModal is itself edit-gated.
 */
export function usePlanLibraryDocs(
  companyId: string,
  segmentReport: SegmentReport | null,
  enabled = true
) {
  const companyQuery = useQuery({
    queryKey: ["sa-company-report-library", companyId],
    queryFn: () => getCompanyReport(companyId),
    enabled: enabled && Boolean(companyId),
    staleTime: 60_000,
  })

  return useMemo(
    () => mergePlanLibraryDocs(companyQuery.data?.report, segmentReport),
    [companyQuery.data?.report, segmentReport]
  )
}
